<template>
  <div class="flex flex-col items-center justify-center bg-white min-h-screen">
    <!--Heading-->
    <div class="mb-20 z-0 md:z-10">
      <div class="flex flex-col">
        <div class="text-center mx-auto">
          <Icon
            icon="heroicons:home-solid"
            class="text-red-600 mx-auto text-4xl md:text-6xl"
          />
        </div>
        <div class="relative text-center"> 
          <h1
            class="text-center text-xl md:text-2xl font-bold text-gray-800 mb-2"
          >
            Request Evidence of Insurance
          </h1>
           <!-- Added Cancel/Back Button -->
          <button 
              @click="goBack"
              class="absolute top-0 right-4 text-sm text-gray-500 hover:text-gray-700 hover:underline"
              title="Cancel and go back"
          >
              Cancel
          </button>
        </div>
      </div>
      <!-- Replace Success Message -->
      <FormSuccessMessage
        v-if="formSubmitted"
        @go-back="goBack"
      />
      <!--Form Container-->
      <div class="max-w-2xl mx-auto bg-white p-8 rounded-xl shadow-xl">
        <!--Contact Information Step 1-->
        <form v-if="!formSubmitted" @submit.prevent="onSubmit">
          <h3 class="text-lg font-bold text-center mb-6 text-gray-800">
            Lender Contact Information
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                for="first-name"
                class="block text-sm font-semibold text-gray-700 mb-2"
                >First Name</label
              >
              <input
                v-model="firstName"
                type="text"
                id="first-name"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring focus:ring-blue-200"
              />
              <p v-if="errors.firstName" class="text-red-500 text-xs italic">
                {{ errors.firstName }}
              </p>
            </div>
            <div>
              <label
                for="last-name"
                class="block text-sm font-semibold text-gray-700 mb-2"
                >Last Name</label
              >
              <input
                v-model="lastName"
                type="text"
                id="last-name"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring focus:ring-blue-200"
              />
              <p v-if="errors.lastName" class="text-red-500 text-xs italic">
                {{ errors.lastName }}
              </p>
            </div>
            <div class="col-span-1 md:col-span-2">
              <label
                for="email"
                class="block text-sm font-semibold text-gray-700 mb-2"
                >Work Email</label
              >
              <input
                id="email"
                v-model="email"
                type="email"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring focus:ring-blue-200"
              />
              <p v-if="errors.email" class="text-red-500 text-xs italic">
                {{ errors.email }}
              </p>
            </div>
            <div>
              <label
                for="last-name"
                class="block text-sm font-semibold text-gray-700 mb-2"
                >Fax Number</label
              >
              <input
                v-model="faxNumber"
                type="text"
                id="last-name"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring focus:ring-blue-200"
              />
              <p v-if="errors.faxNumber" class="text-red-500 text-xs italic">
                {{ errors.faxNumber }}
              </p>
            </div>
            <div>
              <label
                for="phone-number"
                class="block text-sm font-semibold text-gray-700 mb-2"
                >Phone Number</label
              >
              <InputMask
                id="phone-number"
                mask="(999) 999-9999"
                v-model="phoneNumber"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring focus:ring-blue-200"
              ></InputMask>
              <p v-if="errors.phoneNumber" class="text-red-500 text-xs italic">
                {{ errors.phoneNumber }}
              </p>
            </div>
            <div class="col-span-1 md:col-span-2">
              <PolicyNumberInput v-model="policyNumber" />
              <p v-if="errors.policyNumber" class="text-red-500 text-xs italic">
                {{ errors.policyNumber }}
              </p>
            </div>
            <div class="mt-6 col-span-1 md:col-span-2">
              <DocumentUpload 
                v-model="uploadedFile"
                heading="Upload your borrower's authorization form"
                id="borrower-auth-upload-proof"
                :error="documentUploadError" 
              />
            </div>
            <div class="col-span-1 md:col-span-2 flex justify-center">
              <button
                type="submit"
                class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    <!--Background Images-->
    <div class="hidden md:flex justify-between w-full mt-[-300px]">
      <img
        :src="heroHomeLeftImage"
        alt=""
        class="absolute bottom-0 left-0 h-32 md:h-40 xl:h-48 opacity-40 ml-4"
      />
      <div class="flex-grow"></div>
      <img
        :src="heroHomeRightImage"
        alt=""
        class="absolute bottom-0 right-0 h-32 md:h-40 xl:h-48 opacity-40 mr-4"
      />
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { InputMask } from '@/components/ui';
import { useForm, useField } from 'vee-validate';
import * as yup from 'yup';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { v4 as uuidv4 } from 'uuid';
import FormSuccessMessage from '../common/FormSuccessMessage.vue';
import PolicyNumberInput from '../common/PolicyNumberInput.vue';
import DocumentUpload from '../common/DocumentUpload.vue';
import heroHomeLeftImage from '@/assets/images/products/hero/home/hero-home-LLeft.svg';
import heroHomeRightImage from '@/assets/images/products/hero/home/hero-home-LRight.svg';

export default {
  name: 'RequestProofOfInsurance',
  components: {
    Icon,
    InputMask,
    FormSuccessMessage,
    PolicyNumberInput,
    DocumentUpload,
  },
  setup() {
    const { handleSubmit, errors } = useForm();
    const formSubmitted = ref(false); // State for form submissio
    // Form fields
    const { value: firstName } = useField(
      'firstName',
      yup.string().required('First name is required'),
    );
    const { value: lastName } = useField(
      'lastName',
      yup.string().required('Last name is required'),
    );
    const { value: email } = useField(
      'email',
      yup.string().email('Must be a valid email').required('Email is required'),
    );
    const { value: faxNumber } = useField('faxNumber', yup.string());
    const { value: phoneNumber } = useField(
      'phoneNumber',
      yup.string().required('Phone number is required'),
    );
    const { value: policyNumber } = useField(
      'policyNumber',
      yup.string().required('Policy number is required'),
    );

    // File upload state - simplified to just the v-model ref
    const uploadedFile = ref(null); // for v-model
    const documentUploadError = ref('');

    // Form submission
    const onSubmit = handleSubmit(async (values) => {
      console.log('Submitting form', uploadedFile.value); // Debugging line
      if (!uploadedFile.value) {
        console.log('No document uploaded'); // Debugging line
        documentUploadError.value = 'Uploading the document is required.';
        return;
      } else {
        documentUploadError.value = ''; // Reset error message if the check passes
      }

      const requestId = uuidv4();
      let formData = new FormData();
      formData.append('first_name', values.firstName);
      formData.append('last_name', values.lastName);
      formData.append('email', values.email);
      formData.append('fax_number', values.faxNumber);
      formData.append('phone_number', values.phoneNumber);
      formData.append('policy_number', values.policyNumber);
      formData.append('request_id', requestId);
      formData.append('document', uploadedFile.value);

      try {
        // API call
        const response = await axios.post(
          '/api/submit-insurance-request',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );

        console.log('API Response:', response.data);
        formSubmitted.value = true;
        triggerConfetti();
      } catch (error) {
        // Log the error for internal debugging
        console.error('API Call Error:', error);

        // Display a user-friendly error message
        if (error.response) {
          // Handle HTTP errors (e.g., validation errors from the server)
          if (error.response.status === 422 && error.response.data.errors) {
            // Handling validation errors specifically
            let userFriendlyErrors = 'Please check your input:';
            Object.keys(error.response.data.errors).forEach((key) => {
              userFriendlyErrors += `\n - ${error.response.data.errors[key].join(', ')}`;
            });
            alert(userFriendlyErrors);
          } else {
            // For other HTTP errors, provide a generic but helpful message
            alert(
              "We're unable to process your request at this time. Please try again later.",
            );
          }
        } else if (error.request) {
          // The request was made but no response was received
          alert(
            "We're experiencing technical difficulties. Please check your internet connection or try again later.",
          );
        } else {
          // An error occurred in setting up the request
          alert('An unexpected error occurred. Please try again.');
        }
      }
    });

    // Function to trigger confetti
    function triggerConfetti() {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    }

    // ADDED: Method to navigate back using router
    const goBack = () => {
      if (window.history.length > 1) {
          window.history.back();
      } else {
          window.location.href = '/customerservice'; // Adjust fallback if needed
      }
    };

    return {
      errors,
      firstName,
      lastName,
      email,
      faxNumber,
      phoneNumber,
      policyNumber,
      onSubmit,
      documentUploadError,
      uploadedFile,
      formSubmitted,
      goBack, // Expose goBack
      heroHomeLeftImage,
      heroHomeRightImage,
    };
  },
};
</script>

<style scoped></style>
